"use client";

import { useEffect } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { trackMetaPageView } from "src/utils/meta-tracking.util";
import { captureGclidFromUrl } from "src/utils/gclid-tracker";
import { analyticsPath, analyticsUrl } from "./analytics-url";

const GTAG_ANALYTICS = process.env.NEXT_PUBLIC_GTAG_ANALYTICS || "";

/**
 * App Router has no `router.events.on('routeChangeComplete', ...)` — pageview
 * tracking has to be wired by watching `usePathname` + `useSearchParams`.
 * Mounted once in `app/layout.tsx`, this fires GA, FB Pixel and Meta
 * Conversions API on every client-side navigation, and re-captures gclid
 * for any URL that lands a fresh ad-click.
 *
 * `enabled` is decided server-side from the request Host by the layout: on
 * stage and local it is false and this does nothing at all.
 */
export function AnalyticsRouteTracker({ enabled }: { enabled: boolean }) {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (!enabled) return;
    if (typeof window === "undefined") return;
    const search = searchParams?.toString();
    // Scrubbed: the guest session token rides in the query string of every
    // checkout page and must not be handed to GA or Meta. See analytics-url.ts.
    const path = analyticsPath(pathname, search);
    const fullUrl = analyticsUrl(window.location.origin, pathname, search);

    const w = window as Window & {
      gtag?: (...args: unknown[]) => void;
      fbq?: (...args: unknown[]) => void;
    };

    // GA
    if (typeof w.gtag === "function" && GTAG_ANALYTICS) {
      w.gtag("config", GTAG_ANALYTICS, { page_path: path });
    }

    // FB Pixel
    if (typeof w.fbq === "function") {
      w.fbq("track", "PageView");
    }

    // Meta Conversions API
    trackMetaPageView(fullUrl, document.title);

    // gclid: re-run capture in case the navigation came in with a fresh ad-click param
    captureGclidFromUrl();
  }, [enabled, pathname, searchParams]);

  return null;
}
